import React from "react";

/**
 * Last-resort error boundary so the user NEVER sees a blank screen.
 * Renders a self-contained fallback (no Tailwind dependency in case the
 * style sheet itself failed to load) with a "Reload" button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(err) {
    return {
      hasError: true,
      message: (err && err.message) || String(err) || "Unknown error",
    };
  }

  componentDidCatch(err, info) {
    // Surface the error so it shows up in browser DevTools / Sentry.
    console.error("[ErrorBoundary]", err, info);
    try {
      this.setState({ stack: (err && err.stack) || "" });
    } catch (setStateErr) {
      // Extremely defensive — setState can throw if React itself is broken.
      console.debug("[ErrorBoundary] setState failed:", setStateErr?.message);
    }
  }

  handleReload = () => {
    try {
      localStorage.removeItem("web_token");
    } catch (storageErr) {
      console.debug("[ErrorBoundary] localStorage clear failed:", storageErr?.message);
    }
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((rs) => Promise.all(rs.map((r) => r.unregister())))
        .catch((swErr) => console.debug("[ErrorBoundary] SW unregister failed:", swErr?.message))
        .finally(() => window.location.replace("/"));
    } else {
      window.location.replace("/");
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Inline styles so we don't depend on any CSS bundle.
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "24px",
          background: "#09090b",
          color: "#fafafa",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{
          fontSize: "44px",
          marginBottom: "8px",
        }}>⚠️</div>
        <h1 style={{
          fontSize: "20px",
          fontWeight: 800,
          marginBottom: "8px",
        }}>Algo se interrumpió</h1>
        <p style={{
          fontSize: "14px",
          color: "#a1a1aa",
          maxWidth: "420px",
          marginBottom: "20px",
        }}>
          La app no pudo terminar de cargar. Esto suele pasar dentro del
          navegador de Telegram o WhatsApp. Recarga o abre el link directamente
          en Chrome / Safari.
        </p>
        <button
          onClick={this.handleReload}
          data-testid="error-boundary-reload"
          style={{
            background: "#00E5FF",
            color: "#000",
            fontWeight: 700,
            border: "none",
            borderRadius: "8px",
            padding: "12px 24px",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >Recargar IARisk</button>
        {this.state.message && (
          <pre style={{
            marginTop: "16px",
            fontSize: "11px",
            color: "#71717a",
            maxWidth: "90vw",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>{this.state.message}</pre>
        )}
      </div>
    );
  }
}
